import { LAYOUT_CONSTANTS, PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context";
import { canRoleBypassGuardrails } from "@src/utils/role-guardrails";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Checkbox } from "@uhg-abyss/web/ui/Checkbox";
import { Text } from "@uhg-abyss/web/ui/Text";
import type { ReactNode } from "react";

export const FooterLayout = styled("div", {
  width: "100%",
  maxWidth: LAYOUT_CONSTANTS.maxFormWidth,
  fontSize: "14px",
  textAlign: "left",
  padding: "0 8px",
  variants: {
    isMobile: {
      true: {
        backgroundColor: "$app.color.surface.container.secondary",
        padding: "0 20px 24px",
      },
    },
  },
});

const CheckboxContainer = styled("div", {
  width: "100%",
  textAlign: "left",
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  marginTop: "24px",
  color: "inherit",
  variants: {
    hasError: {
      true: {
        color: "$app.color.text.status.error",
      },
    },
  },
});

const FooterLink = styled("a", {
  color: "$app.color.patient.information.footer.link.rest",
  textDecoration: "underline",
  "&:hover": {
    color: "$app.color.patient.information.footer.link.hover",
    textDecoration: "none",
  },
  variants: {
    hasError: {
      true: {
        color: "$app.color.text.status.error !important",
      },
    },
  },
});

export const FormFooter = (step: number) => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { data, setData } = useApplicationContext();
  const isPatientNavigator = canRoleBypassGuardrails(data.userRole);
  const asCheckboxLabel = (label: ReactNode): string => {
    // Abyss Checkbox types require label: string, but runtime supports rich JSX labels.
    return label as unknown as string;
  };

  // Get checkbox states from context or default to false
  const termsChecked = data.termsChecked || false;
  const communicationChecked = data.communicationChecked || false;
  const footerTermsCheckboxError = data.footerTermsCheckboxError || false;
  const footerCommunicationCheckboxError = data.footerCommunicationCheckboxError || false;

  const handleCheckboxChange = (checkboxName: string, isChecked: boolean) => {
    if (checkboxName === "termsChecked") {
      setData("termsChecked", isChecked);
      setData("footerTermsCheckboxError", !isChecked);
    } else if (checkboxName === "communicationChecked") {
      setData("communicationChecked", isChecked);
      setData("footerCommunicationCheckboxError", !isChecked);
    }
  };

  if (step === 2 && !isPatientNavigator) {
    return (
      <FooterLayout isMobile={isMobile}>
        <CheckboxContainer hasError={footerTermsCheckboxError || footerCommunicationCheckboxError}>
          <Checkbox
            data-testid="test-id-patient-information-terms-checkbox"
            id="obc-patient-information-form-footer-terms-checkbox"
            data-obc-label="Patient Information Form Footer Terms Checkbox"
            label={asCheckboxLabel(
              <Text
                css={
                  footerTermsCheckboxError
                    ? { color: "$app.color.text.status.error", fontWeight: 400 }
                    : { fontWeight: 400 }
                }
              >
                I agree to Optum Behavioral Care's{" "}
                <FooterLink
                  id="obc-patient-information-form-footer-terms-conditions-link"
                  data-obc-label="Patient Information Form Footer Terms & Conditions Link"
                  href={TERMS_OF_USE_URL}
                  target="_blank"
                  rel="noreferrer"
                  hasError={footerTermsCheckboxError}
                >
                  Terms & Conditions
                </FooterLink>{" "}
                and{" "}
                <FooterLink
                  id="obc-patient-information-form-footer-privacy-policy-link"
                  data-obc-label="Patient Information Form Footer Privacy Policy Link"
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                  hasError={footerTermsCheckboxError}
                >
                  Privacy policy
                </FooterLink>
                .
              </Text>,
            )}
            value="Unique value here"
            isChecked={!!termsChecked}
            onChange={(event) => {
              return handleCheckboxChange("termsChecked", event.target.checked);
            }}
            css={
              footerTermsCheckboxError
                ? {
                    borderColor: "$app.color.text.status.error",
                    color: "$app.color.text.status.error",
                  }
                : {}
            }
          />
          <Checkbox
            data-testid="test-id-patient-information-communication-checkbox"
            id="obc-patient-information-form-footer-communication-checkbox"
            data-obc-label="Patient Information Form Footer Communication Checkbox"
            label={asCheckboxLabel(
              <Text
                css={
                  footerCommunicationCheckboxError
                    ? { color: "$app.color.text.status.error", fontWeight: 400 }
                    : { fontWeight: 400 }
                }
              >
                I consent to be contacted by email, SMS, and phone calls for updates and information
                related to my care, based on my communication preference.
              </Text>,
            )}
            isChecked={!!communicationChecked}
            onChange={(event) => {
              return handleCheckboxChange("communicationChecked", event.target.checked);
            }}
            css={
              footerCommunicationCheckboxError
                ? {
                    borderColor: "$app.color.text.status.error",
                    color: "$app.color.text.status.error",
                  }
                : {}
            }
          />
        </CheckboxContainer>
      </FooterLayout>
    );
  } else if (step === 3) {
    return (
      <FooterLayout isMobile={isMobile}>
        <div
          style={{
            width: "100%",
            fontSize: isMobile ? "16px" : "18px",
            paddingTop: isMobile ? "0" : "16px",
            textAlign: isMobile ? "justify" : "left",
          }}
        >
          <span>Appointments can be modified or canceled with at least 48 hours’ notice. </span>
          {!isMobile && <br />}
          <span> Late changes may be subject to a cancellation fee.</span>
        </div>
      </FooterLayout>
    );
  } else {
    return null;
  }
};
